import type {
  Award,
  ClaimRequest,
  Config,
  Pacing,
  SpinResponse,
  WheelResponse,
} from '../../shared/types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** En desarrollo se puede fijar el reloj del servidor con ?now=<ISO> en la url. */
function withClock(path: string): string {
  const now = new URLSearchParams(window.location.search).get('now');
  if (!now) return path;
  return `${path}${path.includes('?') ? '&' : '?'}now=${encodeURIComponent(now)}`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(withClock(path), {
    credentials: 'same-origin',
    ...init,
    headers: init.body ? { 'content-type': 'application/json', ...init.headers } : init.headers,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(data?.error ?? res.statusText, res.status);
  return data as T;
}

export interface ConfigResponse {
  config: Config & { awards?: Award[] };
  pacing: Pacing[];
  now: string;
}

export const api = {
  wheel: () => request<WheelResponse>('/api/wheel'),
  spin: () => request<SpinResponse>('/api/spin', { method: 'POST' }),

  session: () => request<{ authed: boolean }>('/api/session'),
  login: (password: string) =>
    request<{ authed: boolean }>('/api/session', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  logout: () => request<{ authed: boolean }>('/api/session', { method: 'DELETE' }),

  getConfig: () => request<ConfigResponse>('/api/config'),
  putConfig: (config: unknown) =>
    request<ConfigResponse>('/api/config', { method: 'PUT', body: JSON.stringify(config) }),

  awards: () => request<{ awards: (Award & { label: string })[] }>('/api/awards'),
  /** Deja los datos del ganador en el premio que acaba de salir. */
  claim: (body: ClaimRequest) =>
    request<{ ok: true }>('/api/awards', { method: 'POST', body: JSON.stringify(body) }),
};
